/**
 * BulkAddCourseSections (Table) — data-fetching layer and selection
 * logic for the Bulk Add Course Section DataGrid.
 *
 * This file sits between the modal orchestrator (index.tsx) and the
 * presentational DataGrid wrapper (BulkSelectCourseTable.tsx).
 *
 * Responsibilities:
 *   - Fires the CDG API query with server-side pagination, search, and
 *     filters — caching is intentionally disabled (`staleTime: 0`,
 *     `gcTime: 0`) so every modal open or filter change hits the
 *     backend.  This guarantees the table always reflects the latest
 *     data and avoids stale-cache issues when sections are created or
 *     deleted between modal opens.
 *   - Derives row IDs from `row.data.sectionId` (the CDG-unique key)
 *     instead of a top-level `id`.  This matches the `getRowId` prop
 *     on the DataGrid and avoids mutating CDG row objects.
 *   - Implements the "Select All across pages" model: when in Select
 *     All mode, every page change auto-selects visible rows minus any
 *     the user has explicitly deselected (`localUnselectedIds`).
 *   - Defines column definitions with `valueGetter` functions that
 *     reach into the nested `row.data.*` shape returned by the CDG
 *     API — again, without reshaping the source data.
 *
 * Column mapping (CDG field → display):
 *   - `row.data.abbreviation`  → Course ID
 *   - `row.data.title`         → Title
 *   - `row.data.startTimestamp` / `row.data.endTimestamp` → Date Range
 */
import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getClosedSlotSectionListCDG, getPublishedSlotSectionListCDG, getSectionListCDG } from '../../../../APIManager/BulkAddCourseSectionManger'
import { type GridColDef } from '@mui/x-data-grid'
import SelectAllTableOption from './SelectAllTableOption'
import BulkSelectCourseTable from './BulkSelectCourseTable'
import CenteredLoader from './components/CenteredLoader'

/**
 * Mirrors the CDG row shape returned by the backend.  Each row wraps
 * the actual section data inside a `data` object alongside permission
 * and action metadata.  We keep this structure intact and use
 * `valueGetter` / `getRowId` to access nested fields.
 */
interface Row {
  actions: string[]
  data: {
    abbreviation: string
    sectionId: string
    title: string
    endTimestamp: string
    [key: string]: any
  }
  dataPermissions: Record<string, any>
  permissions: string[]
  views: Record<string, any>
}

interface Filter {
  field: string
  op?: string
  value: string | string[]
}

const formatDate = (timestamp: string | undefined): string => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

/**
 * Column definitions for the Bulk Add DataGrid.
 *
 * Because CDG rows nest section fields inside `row.data`, every column
 * uses a `valueGetter` to reach the nested value.  This is the standard
 * CDG-compatible pattern — the alternative (flattening rows before
 * passing them to the DataGrid) would break selection syncing, since
 * `getRowId` also reads from `row.data.sectionId`.
 */
const columns: Array<GridColDef<Row>> = [
  {
    field: 'Course_id',
    headerName: 'Course ID',
    width: 150,
    editable: false,
    valueGetter: (value, row) => row?.data?.abbreviation
  },
  {
    field: 'title',
    headerName: 'Title',
    width: 150,
    editable: false,
    flex: 1,
    valueGetter: (value, row) => row?.data?.title
  },
  {
    field: 'end_date',
    headerName: 'Date Range',
    editable: false,
    flex: 1,
    valueGetter: (value, row) => {
      const startDate = formatDate(row?.data?.startTimestamp)
      const endDate = formatDate(row?.data?.endTimestamp)
      return startDate && endDate ? `${startDate} - ${endDate}` : ''
    }
  }
]
interface BulkAddCourseSectionsProps {
  searchValue?: string
  filters?: Filter[]
  termId?: string
  slotId?: string
  setSelectedRowsData: (data: any) => void
  setSelectAll: (selectAll: boolean) => void
  setUnselectedIds: (ids: string[]) => void
  setAllRowIds: (ids: string[]) => void
  filterTrigger?: number
  /** Bulk Update mode — lists only already-attached sections (is_present=true). */
  isPresent?: boolean
  /** Reports CDG matched total so update-mode Save can confirm the blast radius. */
  onMatchedCountChange?: (count: number) => void
  /**
   * Which list to read. The default catalog source cannot express a survey status — it is not
   * among its filterable keys — so the bulk status actions read the slot's own section list
   * instead: `view=closed` for reopen, `view=published` for go-live. All three accept the same
   * filter/search/sort fields.
   */
  listSource?: 'catalog' | 'closedSlotSections' | 'publishedSlotSections'
  /**
   * Status modes only: whether this Survey Project has opt-out switched on. When it is, opted-out
   * sections are excluded from the listing server-side, so `pagination.totalResults` — the number
   * the confirm step shows for Select All — counts only what the action can actually touch.
   */
  isOptOutSettingEnabled?: boolean
}

function BulkAddCourseSections ({ searchValue = '', filters, termId, slotId, setSelectedRowsData, setSelectAll, setUnselectedIds, setAllRowIds, filterTrigger, isPresent, onMatchedCountChange, listSource = 'catalog', isOptOutSettingEnabled }: BulkAddCourseSectionsProps): React.ReactElement {
  // Visual checkbox state for the current page (sectionId strings).
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(7)

  // isSelectAllMode tracks whether the user activated "Select All".
  // While true, every page change auto-selects all visible rows
  // (minus any in `localUnselectedIds`).
  const [isSelectAllMode, setIsSelectAllMode] = useState(false)

  // localUnselectedIds persists the set of sectionIds the user has
  // explicitly unchecked while in Select All mode.  It survives page
  // changes so that navigating away and back doesn't re-check a row
  // the user intentionally deselected.  On save, these become the
  // `not_in` exclusion list sent to the backend.
  const [localUnselectedIds, setLocalUnselectedIds] = useState<string[]>([])

  // CDG query — intentionally never cached (staleTime + gcTime = 0).
  // Every filter change, page change, or search triggers a fresh request
  // because section data can change between modal opens (e.g. new
  // sections created, sections archived by another admin).
  const { data: CDG, isLoading, error: sectionListError } = useQuery({
    queryKey: ['sectionListCDG', filters, termId, slotId, page, pageSize, searchValue, filterTrigger, isPresent, listSource, isOptOutSettingEnabled],
    queryFn: async () => {
      if (listSource === 'closedSlotSections') {
        return await getClosedSlotSectionListCDG(slotId ?? '', filters ?? [], page, pageSize, searchValue, isOptOutSettingEnabled)
      }
      if (listSource === 'publishedSlotSections') {
        return await getPublishedSlotSectionListCDG(slotId ?? '', filters ?? [], page, pageSize, searchValue, isOptOutSettingEnabled)
      }
      return await getSectionListCDG(filters ?? [], termId, slotId, page, pageSize, searchValue, isPresent)
    },
    enabled: (filterTrigger ?? 0) > 0,
    staleTime: 0,
    gcTime: 0
  })

  const sectionListData = CDG?.data
  const allRows = useMemo(() => {
    return sectionListData?.data ?? []
  }, [sectionListData])

  const pagination = useMemo(() => {
    return sectionListData?.pagination
  }, [sectionListData])

  const matchedTotal = pagination?.totalResults ?? allRows.length

  React.useEffect(() => {
    onMatchedCountChange?.(matchedTotal)
  }, [matchedTotal, onMatchedCountChange])

  // Derive sectionIds from CDG rows — uses the nested path that matches
  // the DataGrid's `getRowId` prop so selection models stay in sync.
  const allRowIds = useMemo(() => allRows.map((row: any) => row.data.sectionId), [allRows])

  // When the user types a new search term or changes filters, jump back
  // to page 1 so they always see the first page of the new result set.
  useEffect(() => {
    setPage(1)
  }, [searchValue, filters])

  React.useEffect(() => {
    setAllRowIds(allRowIds)
  }, [allRowIds, setAllRowIds])

  // Persistent Select All across pagination:
  // When the user navigates to a new page while in Select All mode,
  // `allRowIds` changes (new page's data).  This effect auto-checks
  // every row on the new page, excluding any the user previously
  // unchecked (tracked in `localUnselectedIds`).  Without this,
  // checkboxes would appear empty on pages the user hasn't visited.
  useEffect(() => {
    if (isSelectAllMode && allRowIds.length > 0) {
      const currentPageSelectedIds = allRowIds.filter((id: string) => !localUnselectedIds.includes(id))
      setSelectedRows(currentPageSelectedIds)
    }
  }, [isSelectAllMode, allRowIds, localUnselectedIds, page])

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) {
      // Case 3: Select all matching filter results (across all pages)
      setLocalUnselectedIds([]) // Clear local unselected tracking
      setSelectedRows(allRowIds) // Select visible rows on current page for UI
      setSelectAll(true) // Backend will select all matching results
      setUnselectedIds([]) // No exclusions initially
      setSelectedRowsData(undefined) // Not needed - selectAll=true handles it
      setIsSelectAllMode(true)
    } else {
      // Deselect all
      setLocalUnselectedIds([])
      setSelectedRows([])
      setSelectAll(false)
      setUnselectedIds([])
      setSelectedRowsData([])
      setIsSelectAllMode(false)
    }
  }

  /**
   * Handles individual row checkbox toggles from the DataGrid.
   *
   * Two distinct code paths:
   *
   * **Path 1 — Manual mode** (`isSelectAllMode === false`):
   *   The array of checked IDs is the complete selection.  Straightforward.
   *
   * **Path 2 — Select All mode** (`isSelectAllMode === true`):
   *   We don't store every selected ID (could be thousands).  Instead we
   *   maintain an *exclusion list* (`localUnselectedIds`) — the set of
   *   IDs the user has explicitly unchecked.  On save the backend
   *   receives "select all matching filters EXCEPT these IDs" via a
   *   `not_in` filter.
   *
   *   The merge logic:
   *     1. Start with unselected IDs from *other* pages (anything not
   *        in the current page's `allRowIds`).
   *     2. Add newly unchecked IDs from the current page.
   *     3. Remove any IDs the user just re-checked (they were in the
   *        exclusion list but are now back in `ids`).
   */
  const handleSelectionChange = (ids: string[]): void => {
    setSelectedRows(ids)

    if (!isSelectAllMode) {
      setSelectedRowsData(ids)
      setSelectAll(false)
      setUnselectedIds([])
      setLocalUnselectedIds([])
      return
    }

    const currentPageUnselected = allRowIds.filter((id: string) => !ids.includes(id))
    const currentPageReselected = ids.filter((id: string) => localUnselectedIds.includes(id))

    const updatedUnselectedIds = [
      ...localUnselectedIds.filter((id: string) => !allRowIds.includes(id)),
      ...currentPageUnselected
    ].filter((id: string) => !currentPageReselected.includes(id))

    setLocalUnselectedIds(updatedUnselectedIds)
    setUnselectedIds(updatedUnselectedIds)
    setSelectAll(true)
    setSelectedRowsData(undefined)
  }

  if (isLoading) {
    return <CenteredLoader minHeight={320} />
  }

  const allSelected = allRowIds.length > 0 && allRowIds.every((id: string) => selectedRows.includes(id))

  return (
    <>
      {allRows.length > 0 && (
        <SelectAllTableOption
          allSelected={allSelected}
          onSelectAll={handleSelectAll}
          totalCount={matchedTotal}
        />
      )}
      <BulkSelectCourseTable
        rows={allRows}
        columns={columns}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        page={page}
        pageSize={pageSize}
        rowCount={matchedTotal}
        pagination={pagination}
        onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
          setPage(newPage)
          setPageSize(newPageSize)
        }}
      />
    </>
  )
}

export default BulkAddCourseSections
